import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Building2, Receipt, Users, CalendarCheck, Palette,
  Clock, LogOut, Menu, X, Monitor, ShieldCheck, Inbox, ScrollText, CalendarDays, Wallet, FolderArchive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/Logo";

const NAV = {
  super_admin: [
    { to: "/admin", label: "Empresas", icon: Building2 },
    { to: "/admin/billing", label: "Facturación", icon: Receipt },
    { to: "/admin/leads", label: "Leads", icon: Inbox },
    { to: "/admin/lopd", label: "LOPD", icon: ScrollText },
  ],
  company_admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/employees", label: "Empleados", icon: Users },
    { to: "/schedules", label: "Horarios", icon: Clock },
    { to: "/calendar", label: "Calendario", icon: CalendarDays },
    { to: "/approvals", label: "Aprobaciones", icon: CalendarCheck },
    { to: "/expenses", label: "Gastos", icon: Wallet, feature: "expenses" },
    { to: "/documents", label: "Documentos", icon: FolderArchive },
    { to: "/branding", label: "Marca Blanca", icon: Palette },
    { to: "/lopd", label: "LOPD", icon: ScrollText },
  ],
  gestoria: [
    { to: "/documents", label: "Documentos", icon: FolderArchive },
  ],
  employee: [{ to: "/portal", label: "Mi Portal", icon: Clock }],
};

const ROLE_LABEL = {
  super_admin: "Super-Admin",
  company_admin: "Administrador",
  gestoria: "Gestoría",
  employee: "Empleado",
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [features, setFeatures] = useState(null);

  useEffect(() => {
    if (["company_admin", "gestoria", "employee"].includes(user.role)) {
      api.get("/company/features").then(({ data }) => setFeatures(data)).catch(() => {});
    }
  }, [user.role]);

  const allItems = NAV[user.role] || [];
  const items = allItems.filter((it) => !it.feature || !features || features.sections?.[it.feature]);
  const brandName = user?.company?.name || "Fichando";
  const logo = user?.company?.logo_url;

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 h-16 flex items-center gap-3 border-b border-slate-100">
        {logo ? (
          <img src={logo} alt="logo" className="h-8 w-8 rounded-md object-cover" />
        ) : (
          <LogoMark size={34} />
        )}
        <div className="leading-tight overflow-hidden">
          <div className="font-heading font-bold text-[15px] truncate">{brandName}</div>
          <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400">{ROLE_LABEL[user.role]}</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        {items.map(({ to, label, icon: Icon }) => {
          const active = loc.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                active ? "text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
              }`}
              style={active ? { background: "var(--brand)" } : {}}
            >
              <Icon className="h-[18px] w-[18px]" />
              {label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-100 space-y-2">
        {user.role === "company_admin" && user?.company?.slug && (
          <a
            href={`/kiosk/${user.company.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid="open-kiosk-link"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            <Monitor className="h-[18px] w-[18px]" /> Abrir Quiosco
          </a>
        )}
        <div className="px-3 py-2 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-600">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div className="overflow-hidden">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-xs text-slate-400 truncate">{user.email}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/login"); }}
          className="w-full justify-start text-slate-500 hover:text-red-600"
        >
          <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col fixed inset-y-0">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white shadow-2xl">
            <button className="absolute top-4 right-4" onClick={() => setOpen(false)}>
              <X className="h-5 w-5" />
            </button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="glass-nav sticky top-0 z-30 h-16 border-b border-slate-200/60 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
              <LogoMark size={18} /> Fichando
            </div>
          </div>
          <div className="text-sm text-slate-400 font-medium hidden sm:block">
            {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </header>
        <main className="p-4 lg:p-8 fade-up">{children}</main>
      </div>
    </div>
  );
}
